import { useAuth } from "../auth/useAuth";
import { useEffect, useState } from "react";
import { ProductService } from "../services/ProductService";
import { SupplierService } from "../services/SupplierService";
import { WarehouseService } from "../services/WarehouseService";
import type { KeyValue } from "../models/KeyValue";
import type { PagedResult } from "../models/PagedResult";
export async function loadAllLookupPages<T>(
  load: (skip: number, limit: number) => Promise<PagedResult<T>>,
): Promise<T[]> {
  const rows: T[] = [];
  for (let skip = 0; ;) {
    const page = await load(skip, 200);
    rows.push(...page.Data);
    skip += page.Data.length;
    if (page.Data.length === 0 || skip >= page.TotalRecords) return rows;
  }
}
export function useInventoryLookups(includeSuppliers = false) {
  const {hasPermission} = useAuth();
  const canReadProducts = hasPermission("Product.View");
  const [data, setData] = useState<{
    products: KeyValue[];
    suppliers: KeyValue[];
    warehouses: KeyValue[];
    error: string;
    loading: boolean;
  }>({ products: [], suppliers: [], warehouses: [], error: "", loading: true });
  useEffect(() => {
    let active = true;
    void Promise.all([
      canReadProducts ? loadAllLookupPages(ProductService().GetActivePartial) : Promise.resolve([]),
      loadAllLookupPages(WarehouseService().GetPartial),
      includeSuppliers
        ? loadAllLookupPages(SupplierService().GetPartial)
        : Promise.resolve([]),
    ])
      .then(([products, warehouses, suppliers]) => {
        if (active)
          setData({
            products,
            warehouses,
            suppliers,
            error: "",
            loading: false,
          });
      })
      .catch(() => {
        if (active)
          setData((x) => ({
            ...x,
            error:
              "Could not load products, warehouses or suppliers. Reload this page to try again.",
            loading: false,
          }));
      });
    return () => {
      active = false;
    };
  }, [includeSuppliers, canReadProducts]);
  return data;
}
